import { ApolloProvider } from '@apollo/client';
import { AppProps } from 'next/app';
import { DefaultTheme, GlobalStyle } from 'beek-design-system/defaults';
import { MDXProvider } from '@mdx-js/react';
import { NextIntlProvider } from 'next-intl';
import { ThemeProvider } from 'styled-components';
import { useEffect } from 'react';
import { useRouter } from 'next/router';
import MarkDownComponents from 'beek-design-system/Elements/Markdown/Components';
import NProgress from 'nprogress';

import { BeekUserRole } from 'src/utils/user';
import {
  authToken,
  deleteAuthToken,
  resetAuthTokenExpiration,
} from 'src/app/auth/cookies';
import { useApollo } from 'src/api/apolloClient/apollo';
import Toasts from 'src/components/Common/Toasts';
import useAnalytics from 'src/tracking/useAnalytics';

import '@beek/design-system/dist/styles.css';
import 'src/styles/nprogress.css';
import { useChallengeCookie } from 'src/utils/useChallengeCookie';

declare global {
  interface Window {
    gtag: any; // eslint-disable-line
    dataLayer: any; // eslint-disable-line
  }
}

const App = ({ Component, pageProps }: AppProps): JSX.Element => {
  const apolloClient = useApollo();
  const router = useRouter();

  // Whenever the page loads, refresh the auth token to extend the expiration
  useEffect(() => {
    resetAuthTokenExpiration();
  }, []);

  // Start of mix cookies fix
  // ---
  // This code will fix mixed cookies error, where token cookies had role value assigned instead of the token one.
  // The fix involves detecting bad cookies (those with a role value) and forcing a logout by deleting the cookie and refreshing the page
  // We should be deleting this code later.
  useEffect(() => {
    const token = authToken();

    if (token === BeekUserRole.creator || token === BeekUserRole.user) {
      deleteAuthToken();
      router.reload();
    }
  }, [router]);
  // End of mixed cookies fix

  // Init analytics
  useAnalytics();

  // Saves challenge id from attribution link to redirect to the challenges onboarding if new user
  useChallengeCookie();

  // For the Page Transition Loading Bar
  NProgress.configure({ showSpinner: false, speed: 400, trickleSpeed: 100 });
  useEffect(() => {
    router.events.on('routeChangeStart', NProgress.start);
    router.events.on('routeChangeComplete', NProgress.done);
    router.events.on('routeChangeError', NProgress.done);

    return (): void => {
      router.events.off('routeChangeStart', NProgress.start);
      router.events.off('routeChangeComplete', NProgress.done);
      router.events.off('routeChangeError', NProgress.done);
    };
  }, [router]);

  return (
    <ApolloProvider client={apolloClient}>
      <ThemeProvider theme={DefaultTheme}>
        <GlobalStyle />
        <Toasts />
        <NextIntlProvider
          formats={{
            dateTime: {
              short: {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              },
            },
          }}
          messages={pageProps.messages}
          now={new Date(pageProps.now)}
          defaultTranslationValues={{
            strong: (children) => (
              <strong style={{ fontWeight: 700 }}>{children}</strong>
            ),
            em: (children) => (
              <em style={{ fontStyle: 'italic' }}>{children}</em>
            ),
          }}
        >
          <MDXProvider components={MarkDownComponents}>
            <Component {...pageProps} />
          </MDXProvider>
        </NextIntlProvider>
      </ThemeProvider>
    </ApolloProvider>
  );
};

export default App;
